import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Upload, Alert, Button, message } from 'antd';
import { CloudUploadOutlined, FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';

const UploadArea = styled(Box)`
	border: 2px dashed #d9d9d9;
	border-radius: 8px;
	padding: 32px 20px;
	text-align: center;
	background: #fafafa;
	margin-bottom: 16px;
`;

const FileRow = styled(Flex)`
	align-items: center;
	gap: 10px;
	justify-content: center;
	font-size: 13px;
	color: #18405d;
`;

const TemplateLink = styled(Button)`
	padding: 0;
	height: auto;
	color: #18405d;
`;

// S3 prefix for uploaded sheets. Both invoice flows share it, so it lives with
// the modal rather than being redeclared per screen.
export const BULK_TRIP_IDS_ENTITY = 'invoice_bulk_trip_ids_v2';
export const TRIP_ID_HEADER = 'Trip ID';
export const MAX_TRIP_IDS = 10000;
// No '.xls': the backend's pandas read needs xlrd for legacy BIFF, which is
// not installed, so offering it would only 500 after the upload.
const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];
const TEMPLATE_FILE_NAME = 'bulk-trip-fetch-template.csv';

/** Header row only — a sample Trip ID would eventually get submitted verbatim. */
const downloadTemplate = () => {
	const blob = new Blob([`${TRIP_ID_HEADER}\n`], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = TEMPLATE_FILE_NAME;
	link.click();
	URL.revokeObjectURL(url);
};

const hasAcceptedExtension = (name) =>
	ACCEPTED_EXTENSIONS.some((ext) => (name || '').toLowerCase().endsWith(ext));

/**
 * Uploads a single-column Trip ID sheet so finance can pull hundreds of trips
 * into an invoice instead of ticking them one by one. Shared by both invoice
 * screens. `onUpload` owns the S3 round trip (the browser has no .xlsx parser)
 * and must throw so a rejected file keeps the modal open with the reason inline.
 */
export default function BulkTripFetchModal({ open, onCancel, onUpload, contextLabel }) {
	const [file, setFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState('');

	const reset = () => {
		setFile(null);
		setUploading(false);
		setError('');
	};

	const handleClose = () => {
		if (uploading) return;
		reset();
		onCancel();
	};

	const handleBeforeUpload = (selected) => {
		if (!hasAcceptedExtension(selected.name)) {
			message.error('Unsupported file format. Please upload a .csv or .xlsx file.');
			return Upload.LIST_IGNORE;
		}
		setFile(selected);
		setError('');
		return false; // upload happens on confirm, not on select
	};

	const handleSubmit = async () => {
		if (!file) {
			message.error('Select a .csv or .xlsx file first.');
			return;
		}
		setUploading(true);
		setError('');
		try {
			await onUpload(file);
			reset();
			onCancel();
		} catch (err) {
			// File-level 422s carry a user-facing message; show it verbatim.
			setError(
				err?.response?.data?.error?.message ||
					err?.message ||
					'The file could not be processed. Please check it and try again.'
			);
		} finally {
			setUploading(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Bulk Fetch Trips"
			onCancel={handleClose}
			destroyOnClose
			width={560}
			centered
			footer={[
				<Button key="cancel" onClick={handleClose} disabled={uploading}>
					Cancel
				</Button>,
				<Button
					key="submit"
					type="primary"
					onClick={handleSubmit}
					disabled={!file || uploading}
					loading={uploading}
				>
					Upload
				</Button>,
			]}
		>
			<Alert
				type="info"
				showIcon
				style={{ marginBottom: 16 }}
				message={
					<Text>
						Upload a file with a single column: <b>{TRIP_ID_HEADER}</b>. Up to{' '}
						{MAX_TRIP_IDS.toLocaleString('en-IN')} Trip IDs per file. Trip IDs may be
						written as <b>IRPL-1234</b>, <b>1234</b> or the trip code.
					</Text>
				}
			/>

			<Flex justifyContent="flex-end" mb={2}>
				<TemplateLink type="link" icon={<DownloadOutlined />} onClick={downloadTemplate}>
					Download template
				</TemplateLink>
			</Flex>

			<Upload.Dragger
				accept={ACCEPTED_EXTENSIONS.join(',')}
				beforeUpload={handleBeforeUpload}
				maxCount={1}
				showUploadList={false}
				disabled={uploading}
			>
				<UploadArea>
					{file ? (
						<FileRow>
							<FileTextOutlined />
							<span>{file.name}</span>
						</FileRow>
					) : (
						<>
							<CloudUploadOutlined style={{ fontSize: 28, color: '#8c8c8c' }} />
							<Box mt={2}>
								<Text style={{ fontSize: 13 }}>
									Click or drag a .csv / .xlsx file here
								</Text>
							</Box>
						</>
					)}
				</UploadArea>
			</Upload.Dragger>

			{contextLabel ? (
				<Text style={{ fontSize: 12, color: '#6b7280' }}>
					Trips will be matched against {contextLabel}.
				</Text>
			) : null}

			{error ? (
				<Alert type="error" showIcon style={{ marginTop: 16 }} message={error} />
			) : null}
		</Modal>
	);
}

BulkTripFetchModal.propTypes = {
	open: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	onUpload: PropTypes.func.isRequired,
	contextLabel: PropTypes.string,
};

BulkTripFetchModal.defaultProps = {
	open: false,
	contextLabel: '',
};
